import { ChevronsUpDown, Copy, LogOut, User, FileText, Upload, BookOpen, Info, Cookie } from "lucide-react";
import { toastOneError, toastOneInfo, toastOneSuccess } from "@/toast";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar";
import { Badge } from "../ui/badge";
import { useNavigate } from "react-router-dom";
import { useAuthProviderV2, useRoutesProvider } from "../../providers/provider-hooks";
import { CONTACT_ECOTIC } from "@/constants/config";
import { useProtocolDeColaborare } from "@/domains/auth/hooks/useProtocolDeColaborare";
import {
    mesajStareProtocol,
    protocolulSePoateCere,
    textMesajProtocol,
} from "@/domains/auth/utils/protocol-descarcare";
import { useEditionDocuments } from "@/shared/hooks/use-edition-documents";
import { hrefTelefon } from "@/shared/utils/contact-ecotic";
import { deschideSetariCookie } from "@/core/consent/cookie-consent";

/**
 * `href`-ul de telefon al contactului din meniu, calculat o data la incarcarea modulului.
 * `''` cand numarul configurat nu are nicio cifra — atunci ramane text, nu link mort.
 */
const TELEFON_HREF = hrefTelefon(CONTACT_ECOTIC.telefon);

/**
 * SE-15: itemul „Descarcare Model Protocol" deschidea un URL Cloudinary STATIC (aceeasi editie
 * hardcodata pentru toata lumea — `DOCUMENT_LINKS.PROTOCOL_COLABORARE`). Acum foloseste acelasi
 * hook ca `CardProtocol` — PDF-ul real, per scoala.
 *
 * Extras in componenta PROPRIE (nu inline in `NavUser`) ca hook-ul sa se monteze DOAR pentru
 * PROFESOR: hook-urile React nu pot fi apelate conditionat de rol in interiorul aceleiasi
 * componente -- randarea conditionata a componentei intregi e singura cale corecta.
 *
 * 🔴 `laCerere` (2026-10-07): meniul e pe FIECARE pagina autentificata, iar o cerere reusita pe ruta
 * protocolului il consemneaza in backend ca descarcat. Cu cererea la montare, pasul 4 al inscrierii
 * se bifa doar pentru ca s-a desenat meniul. Acum itemul nu cere nimic pana la clic: e activ dupa
 * claim-ul din token (scoala aprobata), iar fisierul se aduce abia cand omul il cere. Daca nu vine,
 * i se spune de ce — inainte, itemul ramanea stins, fara nicio explicatie.
 */
function ItemDescarcareProtocol() {
    const { userInfo } = useAuthProviderV2();
    const { cereSiDescarca } = useProtocolDeColaborare({ laCerere: true });
    const aprobat = protocolulSePoateCere(userInfo);

    const laClic = async () => {
        const stare = await cereSiDescarca();
        const mesaj = mesajStareProtocol(stare, 'tu');
        if (!mesaj) return;
        const text = textMesajProtocol(mesaj, CONTACT_ECOTIC.telefon);
        if (mesaj.ton === 'eroare') toastOneError(text);
        else toastOneInfo(text);
    };

    return (
        <DropdownMenuItem
            // Neaprobat: itemul ramane stins, dar clicul explica de ce (si nu inchide meniul).
            onClick={(e) => {
                if (!aprobat) e.preventDefault();
                void laClic();
            }}
            aria-disabled={!aprobat}
            className={!aprobat ? 'cursor-not-allowed opacity-50' : undefined}
        >
            <FileText />
            Descarcare Model Protocol
        </DropdownMenuItem>
    );
}

export function NavUser() {
    const { isMobile } = useSidebar();
    const { userInfo, logout } = useAuthProviderV2();
    const { NAV_LINKS_V2, PAGES, getMainPresentationRole, ROLES } = useRoutesProvider();
    const navigate = useNavigate();

    const userName = userInfo?.nume || "User";
    const userRole = getMainPresentationRole(userInfo?.role as any);
    // SE-14: linkuri din backend (editia curenta), cu fallback pe DOCUMENT_LINKS -- vezi hook.
    const documente = useEditionDocuments();

    /**
     * SE-24: codul de profesor era vizibil DOAR dupa ce deschideai dropdownul, ca eticheta
     * pasiva. Profesorul il dicteaza elevilor la fiecare ora — deci sta acum si pe badge, si
     * se poate copia cu un tap.
     *
     * Conditia e pe rol SI pe existenta claim-ului: `codProfesor` vine din JWT si lipseste
     * pentru orice alt rol, iar un token de profesor emis inainte de claim-ul asta nu-l are.
     * Fara a doua jumatate a conditiei, badge-ul ar capata o a treia linie goala („Cod: ").
     */
    const codProfesor =
        ROLES.PROFESOR === userInfo?.role && userInfo?.codProfesor ? userInfo.codProfesor : null;

    // Acelasi tipar ca `CollaboratorLinksPage.copy()`: `navigator.clipboard` cere context
    // securizat (https sau localhost), deci pe un telefon conectat la IP-ul de dezvoltare
    // apelul pica. La esec aratam codul in clar — singura iesire ramasa — nu o eroare fara fund.
    const copiazaCodul = async (cod: string) => {
        try {
            await navigator.clipboard.writeText(cod);
            toastOneSuccess("Cod copiat. Elevii îl folosesc la înregistrare.");
        } catch {
            toastOneInfo(`Codul tău: ${cod}`);
        }
    };

    const userInitials = userName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            /* `h-auto min-h-12` DOAR cand apare a treia linie: `size="lg"` fixeaza
                               `h-12` (48px), iar butonul are `overflow-hidden` — adica a treia linie
                               n-ar da scroll, ar disparea TACUT. Fara cod, inaltimea ramane exact
                               cea de azi. */
                            className={`data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground${codProfesor ? " h-auto min-h-12" : ""}`}>
                            <Avatar className="h-8 w-8 rounded-lg">
                                <AvatarImage src="/the-ecoterrian.jpg" alt={userName} />
                                <AvatarFallback className="rounded-lg">{userInitials}</AvatarFallback>
                            </Avatar>
                            {/* `min-w-0`: item de grid intr-un flex; fara el `truncate` n-are de
                                unde taia si latimea urca in parinte (acelasi defect ca pe pagina
                                de colaboratori). */}
                            <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-medium">{userName}</span>
                                <span className="truncate text-xs">{userRole}</span>
                                {codProfesor && (
                                    <span className="truncate text-xs tabular-nums">
                                        Cod: {codProfesor}
                                    </span>
                                )}
                            </div>
                            <ChevronsUpDown className="ml-auto size-4 shrink-0" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                        side={isMobile ? "bottom" : "right"}
                        align="end"
                        sideOffset={4}>
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                <Avatar className="h-8 w-8 rounded-lg">
                                    <AvatarImage src="/the-ecoterrian.jpg" alt={userName} />
                                    <AvatarFallback className="rounded-lg">{userInitials}</AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-medium">{userName}</span>
                                    <span className="truncate text-xs">{userRole}</span>
                                </div>
                            </div>
                        </DropdownMenuLabel>

                        {/* School Points & Professor Code */}
                        {[ROLES.PROFESOR, ROLES.ELEV].includes(userInfo?.role as any) &&
                            userInfo?.points !== undefined && (
                                <DropdownMenuLabel className="flex items-center gap-2 px-2 py-1.5">
                                    <span className="text-xs">Punctele Scolii:</span>
                                    <Badge variant="secondary">{userInfo.points}</Badge>
                                </DropdownMenuLabel>
                            )}
                        {codProfesor && (
                            <>
                                <DropdownMenuLabel className="px-2 py-1 text-xs">
                                    Cod: {codProfesor}
                                </DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => void copiazaCodul(codProfesor)}>
                                    <Copy />
                                    Copiază codul
                                </DropdownMenuItem>
                            </>
                        )}

                        <DropdownMenuSeparator />

                        {/* Profile & School Form */}
                        <DropdownMenuGroup>
                            {NAV_LINKS_V2[PAGES.PROFILE].ALLOWED_ROLES.includes(userInfo?.role as any) && (
                                <DropdownMenuItem onClick={() => navigate(NAV_LINKS_V2[PAGES.PROFILE].PATH)}>
                                    <User />
                                    {NAV_LINKS_V2[PAGES.PROFILE].TEXT}
                                </DropdownMenuItem>
                            )}
                            {NAV_LINKS_V2[PAGES.SCHOOL_FORM_V2].ALLOWED_ROLES.includes(userInfo?.role as any) && (
                                <DropdownMenuItem onClick={() => navigate(NAV_LINKS_V2[PAGES.SCHOOL_FORM_V2].PATH)}>
                                    <FileText />
                                    {NAV_LINKS_V2[PAGES.SCHOOL_FORM_V2].TEXT}
                                </DropdownMenuItem>
                            )}
                            {/* 🔴 Garda pe EXISTENTA intrarii, nu doar pe flag: sub `VITE_AUTH_PAROLA`
                                oprit ruta nu se mai inregistreaza in `NAV_LINKS_V2`, iar
                                `NAV_LINKS_V2[PAGES.PASSWORD_CHANGE].PATH` ar fi `undefined.PATH` —
                                adica o exceptie la randarea meniului, nu un link mort. */}
                            {NAV_LINKS_V2[PAGES.PASSWORD_CHANGE] && (
                                <DropdownMenuItem onClick={() => navigate(NAV_LINKS_V2[PAGES.PASSWORD_CHANGE].PATH)}>
                                    <User />
                                    Schimbare Parola
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuGroup>

                        {/* Professor-specific items */}
                        {ROLES.PROFESOR === userInfo?.role && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuGroup>
                                    <ItemDescarcareProtocol />
                                    {NAV_LINKS_V2[PAGES.PROFILE].ALLOWED_ROLES.includes(userInfo?.role as any) && (
                                        <DropdownMenuItem onClick={() => navigate(NAV_LINKS_V2[PAGES.PROFILE].PATH)}>
                                            <Upload />
                                            Incarcare Protocol
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuGroup>
                            </>
                        )}

                        {/* Rules & Mechanism */}
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <DropdownMenuItem onClick={() => window.open(documente.regulament, "_blank")}>
                                <BookOpen />
                                Regulament
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.open(documente.mecanism, "_blank")}>
                                <Info />
                                Mecanism
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={deschideSetariCookie}>
                                <Cookie />
                                Setări cookie-uri
                            </DropdownMenuItem>
                        </DropdownMenuGroup>

                        {/* Contact Info */}
                        {ROLES.ELEV !== userInfo?.role && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="px-2 py-1 text-xs font-normal">
                                    <div className="flex flex-col gap-1">
                                        <span className="font-semibold">Contact Ecotic</span>
                                        <span>Program: {CONTACT_ECOTIC.program}</span>
                                        {/* Link `tel:` explicit: browserul nu are de unde sa stie ca
                                            un text e un numar de telefon. Formatul afisat ramane cel
                                            configurat; `href`-ul il face `hrefTelefon` international. */}
                                        {TELEFON_HREF ? (
                                            <a href={TELEFON_HREF} className="touch-target-link w-fit underline">
                                                {CONTACT_ECOTIC.telefon}
                                            </a>
                                        ) : (
                                            <span>{CONTACT_ECOTIC.telefon}</span>
                                        )}
                                    </div>
                                </DropdownMenuLabel>
                            </>
                        )}

                        {/* Logout */}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => logout()}>
                            <LogOut />
                            Deconectare
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    );
}
